import { ButtonPrimary, Colors, Icon, Text } from '@amelio/ui-library';
import { useState } from 'react';

import { ReplyInput } from './ReplyInput';
import { ReplyItem } from './ReplyItem';
import { UnsentReply } from './UnsentReply';
import { CommentAnswerCard } from '../CommentAnswerCard/CommentAnswerCard';

import type { CommentItemProps } from './types';

import { useAmelioTranslations } from '@/hooks/utils/useAmelioTranslations';

/** Replies and the field to write one sit under the comment at the same indent, within its width. */
const REPLY_INDENT = 'ml-4 md:ml-[8%] md:w-[92%]';

export const CommentItem = ({
    comment,
    isReply = false,
    onReplySubmit,
    isSubmittingReply = false,
    unsentReply,
    className = '',
    dataTestId = 'comment-item',
}: CommentItemProps) => {
    const { t } = useAmelioTranslations();
    const [showReplyInput, setShowReplyInput] = useState<boolean>(false);

    const hasReplies = comment.replies && !isReply && comment.replies.length > 0;
    const canReply = !isReply && Boolean(comment.reviewQuestionAnswerId && onReplySubmit);

    const replyAction = (
        <ButtonPrimary
            onClick={() => setShowReplyInput(true)}
            leftIcon={
                <Icon
                    name={'arrow-back-square'}
                    color={Colors.BASE_600}
                    size={'sm'}
                    className={'mr-2'}
                />
            }
            className={
                'items-center border-none bg-transparent p-0 shadow-none hover:bg-transparent hover:opacity-70'
            }
            dataTestId={`${dataTestId}-reply-action`}
        >
            <Text
                color={Colors.BASE_600}
                size={'small'}
                weight={'medium'}
            >
                {t('questionResult.reply')}
            </Text>
        </ButtonPrimary>
    );

    return (
        <div
            className={`m-2 w-[95%] ${className}`}
            data-testid={dataTestId}
        >
            <CommentAnswerCard
                answer={{
                    user: comment.user,
                    text: comment.text,
                    submittedAt: comment.timestamp,
                    isAnonymousRespondent: comment.isAnonymousRespondent,
                }}
                userAnswer={comment.userAnswer}
                isReply={isReply}
                footer={canReply && !showReplyInput && !hasReplies ? replyAction : undefined}
                dataTestId={`${dataTestId}-card`}
            />

            {hasReplies && (
                <div className={`mt-4 space-y-4 ${REPLY_INDENT}`}>
                    {comment.replies?.map((reply) => (
                        <ReplyItem
                            key={reply.id}
                            reply={reply}
                            dataTestId={`${dataTestId}-reply-${reply.id}`}
                        />
                    ))}
                </div>
            )}

            {canReply && !showReplyInput && hasReplies && (
                <div className={`mt-4 ${REPLY_INDENT}`}>{replyAction}</div>
            )}

            {canReply && showReplyInput && (
                <div className={`my-8 ${REPLY_INDENT}`}>
                    <ReplyInput
                        autoFocus
                        onSubmit={(content) =>
                            onReplySubmit?.(comment.reviewQuestionAnswerId!, content)
                        }
                        isSubmitting={isSubmittingReply}
                        dataTestId={`${dataTestId}-reply-input`}
                    />
                </div>
            )}

            {unsentReply && (
                <div className={`mt-4 ${REPLY_INDENT}`}>
                    <UnsentReply
                        text={unsentReply}
                        dataTestId={`${dataTestId}-unsent-reply`}
                    />
                </div>
            )}
        </div>
    );
};
